<template>
    <tr :class="['rtb-row', { 'is-pending': isPending }]">
        <td class="rtb-row__campaign">
            <a :href="primaryUrl" class="rtb-row__name">{{ campaign.name }}</a>
            <div class="rtb-row__meta">
                <span class="rtb-row__id">#{{ campaign.id }}</span>
                <span v-if="campaign.advertiser">{{ campaign.advertiser.name }}</span>
                <span v-if="campaign.brand">{{ campaign.brand.name }}</span>
            </div>
            <div v-if="campaign.campaign_type === RTB_CAMPAIGN_TYPE.PACKAGE" class="rtb-row__package">
                Пакет: {{ campaign.screen_package?.name || 'не выбран' }}
            </div>
            <div v-if="campaign.campaign_type === RTB_CAMPAIGN_TYPE.POI" class="rtb-row__package">
                Слой: {{ campaign.poi_layer?.name || 'не выбран' }}
            </div>
        </td>

        <td>
            <RtbStatusBadge :status="campaign.display_status" :label="campaign.display_status_label" />
        </td>

        <td class="rtb-row__period">
            <template v-if="campaign.start_date || campaign.end_date">
                <div>{{ formatDate(campaign.start_date) }}</div>
                <div class="rtb-row__muted">до {{ formatDate(campaign.end_date) }}</div>
            </template>
            <span v-else class="rtb-row__muted">Не задан</span>
        </td>

        <td class="rtb-row__money">
            <RtbMoneyLines :columns="campaign.money.columns" show-limit show-today />
            <div v-if="hasBudgetLimit" class="rtb-row__progress" :title="`Израсходовано ${budgetPercent}% общего бюджета`">
                <div :class="['rtb-row__progress-fill', progressTone(budgetPercent)]" :style="{ width: `${budgetPercent}%` }"></div>
            </div>
        </td>

        <td class="rtb-row__impressions">
            <div class="rtb-row__value">
                {{ formatInteger(campaign.money.impressions_total) }}
                <span v-if="campaign.impr_total_limit" class="rtb-row__muted">/ {{ formatInteger(campaign.impr_total_limit) }}</span>
            </div>
            <div v-if="campaign.impr_total_limit" class="rtb-row__progress">
                <div :class="['rtb-row__progress-fill', progressTone(impressionsPercent)]" :style="{ width: `${impressionsPercent}%` }"></div>
            </div>
            <div class="rtb-row__muted">
                сегодня {{ formatInteger(campaign.money.impressions_today) }}
                <template v-if="campaign.impr_daily_limit">/ {{ formatInteger(campaign.impr_daily_limit) }}</template>
            </div>
        </td>

        <td class="rtb-row__actions">
            <ActionsMenu :items="menuItems" :disabled="isPending" @select="id => emit('action', id)" />
        </td>
    </tr>
</template>

<script setup>
import { computed } from 'vue';
import ActionsMenu from '../../../ui/ActionsMenu.vue';
import RtbMoneyLines from '../../RtbMoneyLines.vue';
import RtbStatusBadge from './RtbStatusBadge.vue';
import { RTB_CAMPAIGN_TYPE, RTB_DISPLAY_STATUS, RTB_ROW_ACTION } from '../../../../constants/rtbCampaigns';
import { formatInteger } from '../../../../utils/numberFormat';
import { formatDateOnlyInRtbTZ } from '../../../../utils/timezones';

const props = defineProps({
    campaign: { type: Object, required: true },
    isPending: { type: Boolean, default: false },
    /** stats — страница статистики: название ведёт в статистику, а не в редактирование. */
    statsMode: { type: Boolean, default: false },
});

const emit = defineEmits(['action']);

const isRunning = computed(() => props.campaign.user_status === RTB_DISPLAY_STATUS.ACTIVE);
const toggleLabel = computed(() => (isRunning.value ? 'Приостановить' : 'Запустить'));

const primaryUrl = computed(() => (props.statsMode || !props.campaign.abilities.edit
    ? props.campaign.urls.statistics
    : props.campaign.urls.edit));

const primaryMoney = computed(() => props.campaign.money.columns.find(column => column.key === props.campaign.money.primary_key));
const hasBudgetLimit = computed(() => Boolean(primaryMoney.value?.limit));
const budgetPercent = computed(() => Math.round(Math.min(100, props.campaign.money.progress_percent || 0)));
const impressionsPercent = computed(() => Math.round(Math.min(100, (props.campaign.money.impressions_total / props.campaign.impr_total_limit) * 100)));

const progressTone = (percent) => {
    if (percent >= 95) return 'is-danger';
    if (percent >= 80) return 'is-warning';
    return '';
};

const formatDate = date => (date ? formatDateOnlyInRtbTZ(date) : '—');

const menuItems = computed(() => {
    const { abilities, user_status: userStatus } = props.campaign;
    const items = [];

    if (abilities.edit) items.push({ id: RTB_ROW_ACTION.EDIT, label: 'Редактировать' });
    if (abilities.toggle) items.push({ id: RTB_ROW_ACTION.TOGGLE, label: toggleLabel.value });
    else if (abilities.edit && userStatus === RTB_DISPLAY_STATUS.DRAFT) {
        items.push({ id: RTB_ROW_ACTION.TOGGLE, label: 'Запустить', disabled: true, hint: 'Откройте черновик и сохраните его без черновика' });
    }
    items.push(
        { id: RTB_ROW_ACTION.STATISTICS, label: 'Статистика' },
        { id: RTB_ROW_ACTION.EXPORT_SCREENS, label: 'Скачать список экранов' },
    );
    if (abilities.delete) items.push({ id: RTB_ROW_ACTION.DELETE, label: 'Удалить', danger: true });

    return items;
});
</script>

<style scoped lang="scss">
@use '../../../../../scss/pages/admin/admin-tokens' as *;

.rtb-row {
    td {
        padding: $spacing-md $spacing-lg;
        border-bottom: 1px solid $admin-border-color;
        vertical-align: top;
        font-size: $font-size-base;
        color: $admin-text-primary;
    }

    &:hover td { background: $admin-bg-secondary; }

    &.is-pending td { opacity: 0.6; }
}

.rtb-row__campaign {
    min-width: 220px;
}

.rtb-row__name {
    font-weight: 600;
    color: $admin-primary-color;
    text-decoration: none;
    word-break: break-word;

    &:hover { text-decoration: underline; }
}

.rtb-row__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 2px $spacing-sm;
    margin-top: 2px;
    font-size: $font-size-sm;
    color: $admin-text-muted;

    span + span::before {
        content: '·';
        margin-right: $spacing-sm;
        color: $admin-text-light;
    }
}

.rtb-row__id {
    color: $admin-text-light;
}

.rtb-row__package {
    margin-top: 2px;
    font-size: $font-size-sm;
    color: $admin-text-secondary;
}

.rtb-row__period {
    white-space: nowrap;
}

.rtb-row__money {
    min-width: 170px;
}

.rtb-row__impressions {
    min-width: 130px;
    white-space: nowrap;
}

.rtb-row__value {
    font-weight: 500;
}

.rtb-row__muted {
    font-size: $font-size-sm;
    color: $admin-text-muted;
    font-weight: 400;
}

.rtb-row__progress {
    height: 4px;
    margin: 6px 0 4px;
    border-radius: 2px;
    background: $admin-border-color;
    overflow: hidden;
}

.rtb-row__progress-fill {
    height: 100%;
    border-radius: 2px;
    background: #10b981;
    transition: width 0.3s ease;

    &.is-warning { background: #f59e0b; }
    &.is-danger { background: #ef4444; }
}

.rtb-row__actions {
    width: 1%;
    white-space: nowrap;
    text-align: right;
}
</style>
